import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, debounceTime, filter, withLatestFrom } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { Device } from 'src/app/model/device';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import * as EventActions from 'src/app/store/event/event.actions';
import { Site } from 'src/app/model/site';
import { ActivatedRoute } from '@angular/router';
import { EventMessage, EventMessageExport, EventType } from 'src/app/model/event-message';
import { TranslateService } from '@ngx-translate/core';
import { selectEventsAll, selectEventsFilter, selectEventsPaging, selectEventTypes } from 'src/app/store/event/event.selector';
import { selectCurrentSite, selectSites } from 'src/app/store/site/site.selector';
import { selectIsDraftMode } from 'src/app/store/user/user.selector';
import { StandardDesignParameters, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { exportToCsv } from 'src/app/modules/shared/utils/exportData';
import moment from 'moment';
import { EventFilterParams } from 'src/app/model/api/filter-params';
import { minDateLogs } from 'src/app/conf/log';


@Component({
	selector: 'app-events-filters',
	templateUrl: './events-filters.component.html',
	providers: [ConfirmationService, DialogService]
})

export class EventsFiltersComponent implements OnInit, OnDestroy {
	eventTypes$: Observable<EventType[] | undefined>;
	eventTypesSub: Subscription | undefined;
	langChangeSub: Subscription | undefined;
	sites$: Observable<Site[] | undefined>;
	sitesSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription | undefined;
	eventsPaging$: Observable<ItemsPagination | undefined>;
	eventsPagingSub: Subscription | undefined;
	eventsAll$: Observable<EventMessage[] | undefined>;
	eventsAllSub: Subscription | undefined;
	eventsFilter$: Observable<EventFilterParams | undefined>;
	eventsFilterSub: Subscription | undefined;

	paramSub: Subscription;

	eventTypes: EventType[]; // final options array for filter events types
	private rawEventTypes: EventType[] = []; // used to store the original event types before translation

	devices: Device[];
	devices$: Observable<Device[]>;

	typeSub: Subscription;
	devSub: Subscription;
	startDateSub: Subscription;
	endDateSub: Subscription;

	filterSub: Subscription;
	filterGroup: FormGroup;

	today = new Date();
	totalRecords: number;

	MAX_EXPORT_LOG = 10000;

	acceptButtonStyle: string;
	rejectButtonStyle: string;
	rejectButtonVisible: boolean;
	saving = false;
	minDate: Date = new Date();

	// Design
	screenWidth: number;
	MIN_WIDTH_MX7 = 1700;
	MIN_MD_WIDTH = 500;
	MAX_MD_WIDTH = 1120;

	constructor(
		private store: Store<AppState>,
		private route: ActivatedRoute,
		private formBuilder: FormBuilder,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(EventActions.resetFilter());
		this.minDate = minDateLogs();
		this.filterGroup = new FormGroup({
			'deviceId': new FormControl(''),
			'typeId': new FormControl(''),
			'picker': new FormControl(''),
			//'startDate': new FormControl(''),
			//'endDate': new FormControl('')
		});
		this.store.dispatch(EventActions.getEventTypes());

		this.eventTypesSub = this.eventTypes$.subscribe(eventTypes => {
			this.rawEventTypes = eventTypes ?? []; // store the original event types before translation
			this.buildEventTypes();
		});

		this.langChangeSub = this.translate.onLangChange.subscribe(() => {
			this.buildEventTypes();

			const hadTypeSelected = this.f.typeId.value?.Id != null;
			if (hadTypeSelected) { // if a type was selected before the language change, set the '--' default item
				this.f.typeId.setValue(this.eventTypes[0], { emitEvent: false });
				this.store.dispatch(EventActions.setFilter({ typeId: undefined }));
			}
		});

		this.sitesSub = this.sites$
			.pipe(
				withLatestFrom(
					this.isDraftMode$,
					this.site$,
				),
			)
			.subscribe(([sites, isDraftMode, currentSite]) => {
				if (isDraftMode) {
					this.devices = this.concatSiteDevices(sites.filter(s => s.Id === currentSite.Id));
				} else {
					this.devices = this.concatSiteDevices(sites);
				}
				const device = new Device();
				device.Name = '---';
				this.devices.unshift(device);
				const deviceId = this.route.snapshot.queryParams['deviceId'];
				if (typeof deviceId === 'undefined') {
					return;
				}
				const device1 = this.devices.filter(dev => dev.Id === +deviceId);
				if (device1 === undefined || device1.length === 0) {
					return;
				}
				this.f.deviceId.setValue(device1[0]);
				this.store.dispatch(EventActions.setFilter({ deviceId: +deviceId }));
			}
			);

		this.eventsPagingSub = this.eventsPaging$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.setOnFilterChanges();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.eventsFilterSub?.unsubscribe();
		this.filterSub?.unsubscribe();
		this.paramSub?.unsubscribe();
		this.typeSub?.unsubscribe();
		this.devSub?.unsubscribe();
		this.startDateSub?.unsubscribe();
		this.endDateSub?.unsubscribe();
		this.eventTypesSub?.unsubscribe();
		this.langChangeSub?.unsubscribe();
		this.eventsPagingSub?.unsubscribe();
	}

	initSelectors(): void {
		this.eventTypes$ = this.store.select(selectEventTypes);
		this.sites$ = this.store.select(selectSites);
		this.site$ = this.store.select(selectCurrentSite);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
		this.eventsPaging$ = this.store.select(selectEventsPaging);
		this.eventsAll$ = this.store.select(selectEventsAll);
		this.eventsFilter$ = this.store.select(selectEventsFilter);
	}

	onSelectTypes() {
		this.store.dispatch(EventActions.setFilter({ typeId: this.f.typeId.value.Id }));
	}

	onSelectDeviceId() {
		this.store.dispatch(EventActions.setFilter({ deviceId: this.f.deviceId.value.Id }));
	}

	onSelectPicker() {
		const startDate = this.f.picker.value[0];
		const endDate = this.f.picker.value[1];
		if (startDate !== null && endDate !== null) {
			this.store.dispatch(EventActions.setFilter({ startDate: startDate }));
			this.store.dispatch(EventActions.setFilter({ endDate: endDate }));
		} if (startDate !== null && endDate === null) {
			this.store.dispatch(EventActions.setFilter({ startDate: startDate }));
			this.store.dispatch(EventActions.setFilter({ endDate: startDate }));
		}
		//this.store.dispatch(EventActions.setFilter({ deviceId: this.f.deviceId.value.Id }));
	}

	setOnFilterChanges(): void {

		this.filterSub = this.f.picker.valueChanges.pipe(
			debounceTime(500),
		).subscribe(() => {
			this.onSelectPicker();
		}
		);
		/*

		this.startDateSub = this.f.startDate.valueChanges.subscribe(value => {
			this.store.dispatch(EventActions.setFilter({ startDate: new Date(value) }));
		});

		this.endDateSub = this.f.endDate.valueChanges.subscribe(value => {
			this.store.dispatch(EventActions.setFilter({ endDate: new Date(value) }));
		});*/
	}

	concatSiteDevices(sites: Site[]): Device[] {
		let devices: Device[] = [];
		for (const site of sites) {
			if (site.CurrentlyOwned) {
				devices = devices.concat(site.Devices);
			}
		}
		return devices;
	}

	// eventTypeSort is used to sort the event types array by the DescriptionLabel field
	// asc sort
	eventTypeSort(eventTypes: EventType[]): EventType[] {
		return [...eventTypes].sort((a, b) =>
			(a.DescriptionLabel ?? a.Description).localeCompare(b.DescriptionLabel ?? b.Description, undefined, { sensitivity: 'base' })
		);
	}

	// buildEventTypes is used to build the event types array with the translated descriptions
	private buildEventTypes(): void {
		const eventTypes = this.rawEventTypes.map(eventType => ({
			...eventType,
			DescriptionLabel: this.translate.instant(eventType.Description), // DescriptionLabel will be the final field for filter events types
		}));
		this.eventTypes = this.eventTypeSort(eventTypes);
		this.eventTypes.unshift({ // unshift is used to add the new item at the beginning of the array
			Id: undefined,
			Description: '--',
			Category: null,
			DescriptionLabel: '--',
		});
	}

	get f(): any {
		return this.filterGroup.controls;
	}

	exportLimitWarning(): void {
		this.acceptButtonStyle = "p-button-primary";
		this.rejectButtonVisible = false;
		this.confirmationService.confirm({
			header: this.translate.instant('WARNING_EXPORT_LOG_TITLE'),
			message: this.translate.instant('WARNING_EXPORT_LOG_MESSAGE'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => { },
			reject: () => { }
		});
	}

	exportToCsv() {
		if (this.totalRecords > this.MAX_EXPORT_LOG) {
			this.exportLimitWarning();
		} else {
			this.saving = true;
			this.store.dispatch(EventActions.getEventsAll({}));
			this.eventsAllSub = this.eventsAll$.subscribe(
				logs => {
					if (logs !== undefined && logs.length > 0) {
						const data = this.prepareDataForLogs(logs);
						const map = new Map();
						map.set("EventDateLoc", this.translate.instant('EVENT_DATE'));
						map.set("Timezone", this.translate.instant('TIMEZONE'));
						map.set("Username", this.translate.instant('USERNAME'));
						map.set("Device", this.translate.instant('DEVICE'));
						map.set("Type", this.translate.instant('MTMA_DIAG_FILTER_EVENT_TYPE'));
						map.set("Description", this.translate.instant('EVENT'));
						map.set("Message", this.translate.instant('MESSAGE'));
						const eventsStr : string = this.translate.instant('EVENTS').toLowerCase();
						exportToCsv(data, 'exportData - ' + eventsStr, map);
						this.eventsAllSub?.unsubscribe();
						this.store.dispatch(EventActions.getEventsAllReset());
						this.saving = false;
					}
				}
			);
		}
	}

	prepareDataForLogs(logs: EventMessage[]): EventMessageExport[] {
		const list = [];
		for (const log of logs) {
			list.push(this.prepareDataForLog(log));
		}
		return list;
	}

	prepareDataForLog(log: EventMessage): EventMessageExport {
		const logExport = new EventMessageExport();
		logExport.EventDateLoc = moment.utc(log.EventDateLoc).format('DD-MM-yyyy HH:mm:ss');
		logExport.Timezone = log.Timezone;
		logExport.Username = log.Username;
		logExport.Device = log.DeviceId ? log.DeviceId.toString() : '';
		logExport.Type = log.Category;
		logExport.Description = this.translate.instant(log.Description);
		logExport.Message = log.Message;
		return logExport;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	layoutMarginResponsive(): string {
		return (this.screenWidth >= this.MIN_WIDTH_MX7) ? "mx-7" : "mx-2";
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	col4Responsive(): string {
		return StandardPageLayout.col4ResponsiveStd(this.screenWidth);
	}

	buttonRespCol(): string {
		return (this.screenWidth >= StandardDesignParameters.MIN_WIDTH_MX7_LATER) ? "col-12 flex justify-content-center" : (this.screenWidth >= StandardDesignParameters.MIN_WIDTH_COL6) ? 'col-6 flex justify-content-center' : 'col-12 flex justify-content-center';
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}
